import { NFL_TEAMS_TOTAL, NFLDivision } from '@shared/constants';
import { toSeasonWeekID } from '@shared/helpers';
import { ITeamDetails, ITeamStandings, SeasonID, SeasonWeekID, WeekID } from '@shared/types';
import { ConsoleID, getConsoleState } from 'app/config/Logger';
import { Cls, Emojis } from 'app/constants';
import { useAppSelector } from 'app/hooks';
import { getShowStarterScreen } from 'app/slices/AppUiSlice';
import { getSelectedSeasonWeek, getSelectedWeek, getSelectedYear } from 'app/slices/GamesSlice';
import { getTeams } from 'app/slices/LeagueSlice';
import { getSeasonStandings } from 'app/slices/RankingSlice';
import { breakUID } from 'helpers/IdHelpers';
import { sortByOrderedUID } from 'helpers/SortHelpers';
import useDataLoaderForStandings from 'hooks/useDataLoaderForStandings';
import { useCallback, useEffect, useMemo, useState } from 'react';

const CONSOLE_ON = getConsoleState(ConsoleID.UseSeasonStandings);

export type UseSeasonStandingsHook = ReturnType<typeof useSeasonStandings>;
const useSeasonStandings = () => {
  useDataLoaderForStandings();
  const teams: ITeamDetails[] = useAppSelector(getTeams);
  const selectedWeek: WeekID = useAppSelector(getSelectedWeek);
  const selectedYear: SeasonID = useAppSelector(getSelectedYear);
  const selectedSeasonWeekID: SeasonWeekID = useAppSelector(getSelectedSeasonWeek);
  const seasonStandings: ITeamStandings[] = useAppSelector(getSeasonStandings);
  const isStarterDialogVisible = useAppSelector(getShowStarterScreen);

  const [weekStandings, setWeekStandings] = useState<ITeamStandings[]>([]);

  const [dataLoaded, setDataLoaded] = useState<boolean>(false);

  useEffect(() => {
    const count = seasonStandings.length;
    if (count > 0) {
      const selectedSeasonStandings = seasonStandings.filter(
        (standing) => breakUID(standing.uID).yearID === selectedYear,
      );
      const currentSeasonWeekID = toSeasonWeekID(selectedYear, selectedWeek);
      let filteredWeekStandings = selectedSeasonStandings.filter(
        (standing) => breakUID(standing.uID).seasonWeekID === currentSeasonWeekID,
      );
      // if there are no standings available for the currently selected seasonWeekID, show the most recent one whatever that may be.
      if (filteredWeekStandings.length === 0) {
        const len = selectedSeasonStandings.length;
        const startIndex = Math.max(len - NFL_TEAMS_TOTAL, 0);
        // Making an assumption here that we have all 32 teams for whatever the last week in the given year could be
        filteredWeekStandings = selectedSeasonStandings.sort(sortByOrderedUID).slice(startIndex);
      }

      const msg1 = ` Found %c ${count} %c standings, showing %c ${selectedSeasonStandings.length} %c`;
      const msg2 = ` filteredStandings %c ${filteredWeekStandings.length} %c`;
      if (CONSOLE_ON) console.log(`${Emojis.checkMark} ${msg1}${msg2}`, Cls.Cnt, '', Cls.Val, '', Cls.Info, '');

      setWeekStandings([...filteredWeekStandings]);
      setDataLoaded(true);
    } else {
      if (CONSOLE_ON) console.log(`${Emojis.redBrick} Season Standings not loaded yet`);
    }
  }, [seasonStandings, selectedSeasonWeekID, selectedYear, selectedWeek]);

  const canShowStandings = useMemo(() => {
    return !isStarterDialogVisible && !!selectedWeek;
  }, [selectedWeek, isStarterDialogVisible, dataLoaded]);

  const getDivisionStandings = useCallback(
    (division: NFLDivision): ITeamDetails[] => {
      return teams.filter((team) => team.teamDivision === division);
    },
    [teams],
  );

  return {
    selectedWeek,
    weeklyTeamStandings: weekStandings,
    getDivisionStandings,
    canShowStandings,
    standingsLoaded: dataLoaded,
  };
};

export default useSeasonStandings;
